import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import NotificationBell from '../components/NotificationBell';
import SessionTimeoutModal from '../components/SessionTimeoutModal';
import api from '../../services/api';

import {
    RiDashboardLine,
    RiWallet3Line,
    RiFileTextLine,
    RiToolsLine,
    RiBuildingLine,
    RiPercentLine,
    RiUserLine,
    RiMoonLine,
    RiSunLine,
    RiMenu4Line,
    RiArrowLeftSLine,
    RiArrowRightSLine,
    RiLogoutBoxLine,
    RiMoneyDollarCircleLine,
    RiMessage3Line,
    RiGlobalLine,
    RiArrowDownSLine,
    RiMegaphoneLine,
    RiBarChartBoxLine,
    RiBookOpenLine,
    RiFilePaper2Line,
} from 'react-icons/ri';



const OwnerLayout = () => {
    const { t, i18n } = useTranslation();
    const { user, logout } = useAuth();
    const { isDarkMode, toggleDarkMode, isRTL, toggleRTL } = useTheme();
    const navigate = useNavigate();

    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [langOpen, setLangOpen] = useState(false);

    // Responsive Sidebar Logic
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            setSidebarOpen(!mobile);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Live Clock
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 60000); // update every minute
        return () => clearInterval(timer);
    }, []);

    const formattedDate = currentTime.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' });
    const formattedTime = currentTime.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

    // Polling Unread Messages
    useEffect(() => {
        const fetchUnread = async () => {
            try {
                const res = await api.get('/portal/owner/messages/unread-count');
                if (res.data?.success) setUnreadMessages(res.data.count);
            } catch (e) {
                // Ignore gracefully
            }
        };
        fetchUnread();
        const intervalId = setInterval(fetchUnread, 30000);
        return () => clearInterval(intervalId);
    }, []);

    const LANGUAGES = [
        { code: 'en', label: 'English' },
        { code: 'ar', label: 'العربية' },
                { code: 'ku', label: 'کوردی' },
        { code: 'de', label: 'Deutsch' },
        { code: 'fr', label: 'Français' },
        { code: 'it', label: 'Italiano' },
        { code: 'sp', label: 'Español' },
    ];

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
        setLangOpen(false);
        const rtlLangs = ['ar'];
        if (rtlLangs.includes(lng) && !isRTL) toggleRTL();
        else if (!rtlLangs.includes(lng) && isRTL) toggleRTL();
    };

    const getCurrentLanguageDisplay = () => {
        const names = { en: 'English', ar: 'العربية', ku: 'کوردی', de: 'Deutsch', fr: 'Français', it: 'Italiano', sp: 'Español' };
        return names[i18n.language] || 'English';
    };

    const handleLogout = () => {
        logout();
        navigate('/portal/owner/login');
    };

    const menuItems = [
        {
            section: 'core', items: [
                { path: '/portal/owner/dashboard', name: 'Dashboard', icon: <RiDashboardLine size={20} /> },
            ]
        },
        {
            section: 'properties_&_contracts', items: [
                { path: '/portal/owner/properties', name: 'Properties', icon: <RiBuildingLine size={20} /> },
                { path: '/portal/owner/contracts', name: 'Contracts', icon: <RiFileTextLine size={20} /> },
            ]
        },
        {
            section: 'financial', items: [
                { path: '/portal/owner/installments', name: 'Installments', icon: <RiWallet3Line size={20} /> },
                { path: '/portal/owner/services', name: 'Service Charges', icon: <RiPercentLine size={20} /> },
                { path: '/portal/owner/financials', name: 'Financials', icon: <RiMoneyDollarCircleLine size={20} /> },
                { path: '/portal/owner/ledger', name: 'My Ledger', icon: <RiBookOpenLine size={20} /> },
                { path: '/portal/owner/tax-documents', name: 'Tax Documents', icon: <RiFilePaper2Line size={20} /> },
                { path: '/portal/owner/property-report', name: 'Property Reports', icon: <RiBarChartBoxLine size={20} /> },
                { path: '/portal/owner/investment', name: 'Investment', icon: <RiBarChartBoxLine size={20} /> },
            ]
        },
        {
            section: 'maintenance', items: [
                { path: '/portal/owner/maintenance', name: 'Maintenance', icon: <RiToolsLine size={20} /> },
            ]
        },
    ];

    const headerLinks = [
        { path: '/portal/owner/profile', name: 'Profile', icon: <RiUserLine size={20} /> },
        { path: '/portal/owner/messages', name: 'Messages', icon: <RiMessage3Line size={20} />, badge: unreadMessages > 0 ? unreadMessages : null },
        { path: '/portal/owner/announcements', name: 'Announcements', icon: <RiMegaphoneLine size={20} /> },
    ];

    const sidebarVariants = {
        open: { width: 280, x: 0 },
        closed: { width: isMobile ? 280 : 80, x: isMobile ? (isRTL ? '100%' : '-100%') : 0 },
    };

    const textVariants = {
        open: { opacity: 1, x: 0 },
        closed: { opacity: 0, x: -10 },
    };

    const rtlTextVariants = {
        open: { opacity: 1, x: 0 },
        closed: { opacity: 0, x: 10 },
    };

    const getMainContentMargin = () => {
        if (isMobile) return '0px';
        return sidebarOpen ? '280px' : '80px';
    };

    const navItemClass = (isActive) =>
        `flex items-center py-2.5 px-3 rounded-lg transition-colors overflow-hidden ${isActive
            ? isDarkMode
                ? 'bg-red-600 text-white shadow-md shadow-red-900/20'
                : 'bg-red-600 text-white shadow-md shadow-red-500/20'
            : isDarkMode
                ? 'text-zinc-400 hover:text-white hover:bg-zinc-800/70'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
        }`;

    return (
        <div className={`min-h-screen ${isDarkMode ? 'bg-zinc-950' : 'bg-gray-50'} ${isRTL ? 'rtl' : 'ltr'} relative font-sans text-gray-900 dark:text-gray-100`}>

            {/* Sidebar */}
            <motion.aside
                variants={sidebarVariants}
                animate={sidebarOpen ? 'open' : 'closed'}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className={`h-screen ${isDarkMode ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-gray-200'} ${isRTL ? 'border-l' : 'border-r'} flex flex-col shadow-xl fixed z-50`}
                style={{
                    position: 'fixed', top: 0, bottom: 0,
                    [isRTL ? 'right' : 'left']: 0,
                    overflowX: 'hidden', direction: isRTL ? 'rtl' : 'ltr'
                }}
            >
                {/* Sidebar Header */}
                <div className={`flex items-center justify-between h-16 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'} border-b px-4`}>
                    <div className={`flex items-center ${sidebarOpen ? 'gap-3 overflow-hidden' : 'justify-center w-full'}`}>
                        <div className="flex items-center justify-center flex-shrink-0">
                            <img src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"} alt="Logo" className={sidebarOpen ? "h-8 w-auto" : "h-8 w-8 object-contain"} />
                        </div>
                        <AnimatePresence>
                            {sidebarOpen && (
                                <motion.span variants={isRTL ? rtlTextVariants : textVariants} initial="closed" animate="open" exit="closed" className={`text-lg font-bold truncate ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                                    {t('owner_portal')}
                                </motion.span>
                            )}
                        </AnimatePresence>
                    </div>

                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className={`p-1.5 rounded-full hidden md:block ${isDarkMode ? 'hover:bg-zinc-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'} ${!sidebarOpen ? 'hidden' : ''}`}
                    >
                        {isRTL ? <RiArrowRightSLine size={20} /> : <RiArrowLeftSLine size={20} />}
                    </button>

                    <button
                        onClick={() => setSidebarOpen(false)}
                        className={`p-1.5 rounded-full md:hidden ${isDarkMode ? 'hover:bg-zinc-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                    >
                        <RiArrowLeftSLine size={20} />
                    </button>
                </div>

                {!isMobile && !sidebarOpen && (
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className={`p-2 mx-auto mt-2 rounded-full ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-zinc-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                        <RiMenu4Line size={20} />
                    </button>
                )}

                {/* User Profile Summary */}
                <div className={`p-4 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'} border-b`}>
                    <div className={`flex items-center ${sidebarOpen ? '' : 'justify-center'}`}>
                        <div className="h-10 w-10 min-w-[40px] rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-md text-white font-bold">
                            {user?.name?.charAt(0) || 'O'}
                        </div>
                        <AnimatePresence>
                            {sidebarOpen && (
                                <motion.div variants={isRTL ? rtlTextVariants : textVariants} initial="closed" animate="open" exit="closed" className="ms-3 overflow-hidden">
                                    <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{user?.name}</p>
                                    <p className={`text-xs truncate ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('owner')}</p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Sidebar Navigation */}
                <nav className="flex-grow overflow-y-auto overflow-x-hidden scrollbar-thin mt-4">
                    {menuItems.map((section, idx) => (
                        <div key={idx} className="mb-4 flex flex-col items-stretch">
                            <AnimatePresence>
                                {sidebarOpen && (
                                    <motion.div variants={isRTL ? rtlTextVariants : textVariants} initial="closed" animate="open" exit="closed" className={`mb-2 px-4 text-xs font-semibold ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'} uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t(section.section)}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            <ul className="space-y-1 px-4">
                                {section.items.map(item => (
                                    <li key={item.path}>
                                        <NavLink to={item.path} className={({ isActive }) => navItemClass(isActive)}>
                                            <div className="relative flex items-center justify-center w-6 h-6 shrink-0">
                                                {item.icon}
                                                {item.badge && (
                                                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                                        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 border-2 border-white dark:border-zinc-900"></span>
                                                    </span>
                                                )}
                                            </div>
                                            <AnimatePresence>
                                                {sidebarOpen && (
                                                    <motion.span variants={isRTL ? rtlTextVariants : textVariants} initial="closed" animate="open" exit="closed" className="ms-3 text-sm font-medium truncate">
                                                        {t(item.name)}
                                                    </motion.span>
                                                )}
                                            </AnimatePresence>
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    {/* Mobile: Header Links inside Sidebar */}
                    <div className="md:hidden mt-4 pt-4 border-t border-gray-200 dark:border-zinc-800">
                        <ul className="space-y-1 px-4">
                            {headerLinks.map(item => (
                                <li key={item.path}>
                                    <NavLink to={item.path} className={({ isActive }) => navItemClass(isActive)}>
                                        <div className="relative flex items-center justify-center w-6 h-6 shrink-0">
                                            {item.icon}
                                            {item.badge && (
                                                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 border-2 border-white dark:border-zinc-900"></span>
                                                </span>
                                            )}
                                        </div>
                                        <AnimatePresence>
                                            {sidebarOpen && (
                                                <motion.span variants={isRTL ? rtlTextVariants : textVariants} initial="closed" animate="open" exit="closed" className="ms-3 text-sm font-medium truncate">
                                                    {t(item.name)}
                                                </motion.span>
                                            )}
                                        </AnimatePresence>
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                </nav>

                {/* Logout */}
                <div className={`p-4 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'} border-t mt-auto`}>
                    <button onClick={handleLogout} className={`flex items-center w-full py-2.5 px-3 rounded-lg transition-colors overflow-hidden ${isDarkMode ? 'text-zinc-400 hover:text-white hover:bg-red-900/20' : 'text-gray-600 hover:text-red-600 hover:bg-zinc-50'}`}>
                        <span className="flex items-center justify-center w-6 h-6 shrink-0"><RiLogoutBoxLine size={20} /></span>
                        <AnimatePresence>
                            {sidebarOpen && (
                                <motion.span variants={isRTL ? rtlTextVariants : textVariants} initial="closed" animate="open" exit="closed" className="ms-3 text-sm font-medium text-start">
                                    {t('logout')}
                                </motion.span>
                            )}
                        </AnimatePresence>
                    </button>
                </div>
            </motion.aside>

            {/* Mobile Overlay */}
            {isMobile && sidebarOpen && (
                <div className="fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity" onClick={() => setSidebarOpen(false)} />
            )}

            {/* Main Content */}
            <div className="flex flex-col h-screen transition-all duration-300 ease-in-out" style={{ [isRTL ? 'marginRight' : 'marginLeft']: getMainContentMargin() }}>

                {/* Header */}
                <header className={`sticky top-0 z-30 h-16 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} border-b flex items-center justify-between px-4 md:px-6`}>

                    <div className="flex items-center gap-3">
                        <button onClick={() => setSidebarOpen(!sidebarOpen)} className={`md:hidden p-2 rounded-lg ${isDarkMode ? 'hover:bg-zinc-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}>
                            <RiMenu4Line size={20} />
                        </button>
                        <div className="hidden lg:block ml-2">
                            <h2 className={`text-md font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                {t('welcome_user', { name: user?.name?.split(' ')[0] || '' })}
                            </h2>
                            <p className={`text-xs font-medium mt-0.5 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                                {formattedDate} • {formattedTime}
                            </p>
                        </div>
                    </div>

                    {/* Centered Desktop Header Nav */}
                    <nav className="hidden md:flex flex-1 items-center justify-center h-full space-x-2">
                        {headerLinks.map(item => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                className={({ isActive }) => `inline-flex items-center gap-1.5 px-3 h-16 border-b-2 text-sm font-medium transition-colors duration-150 ${
                                    isActive
                                        ? isDarkMode ? 'border-red-500 text-white' : 'border-red-600 text-gray-900'
                                        : isDarkMode ? 'border-transparent text-zinc-400 hover:text-white' : 'border-transparent text-gray-500 hover:text-gray-900'
                                }`}
                            >
                                <div className="relative flex items-center justify-center">
                                    {item.icon}
                                    {item.badge && (
                                        <span className="absolute -top-1 -right-2 flex h-2.5 w-2.5">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border-2 border-white dark:border-zinc-900"></span>
                                        </span>
                                    )}
                                </div>
                                <span className="capitalize">{t(item.name)}</span>
                            </NavLink>
                        ))}
                    </nav>

                    {/* Right Header */}
                    <div className="flex items-center gap-2">
                        <NotificationBell />

                        {/* Language Selector — same as admin Header.jsx */}
                        <div className="relative">
                            <button
                                onClick={() => setLangOpen(o => !o)}
                                className={`p-2 rounded-lg flex items-center space-x-1 transition-colors ${
                                    isDarkMode ? 'hover:bg-zinc-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                                }`}
                            >
                                <RiGlobalLine size={20} />
                                <span className="text-sm font-medium hidden sm:block">{getCurrentLanguageDisplay()}</span>
                                <RiArrowDownSLine size={16} />
                            </button>

                            {langOpen && (
                                <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-48 rounded-lg shadow-lg border z-50 ${
                                    isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'
                                }`}>
                                    <div className="py-1">
                                        {LANGUAGES.map(({ code, label }) => (
                                            <button
                                                key={code}
                                                onClick={() => changeLanguage(code)}
                                                className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${
                                                    i18n.language === code
                                                        ? isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-zinc-50 text-red-600'
                                                        : isDarkMode ? 'text-gray-300 hover:bg-zinc-800' : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <button onClick={toggleDarkMode} className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-zinc-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}>
                            {isDarkMode ? <RiSunLine size={20} /> : <RiMoonLine size={20} />}
                        </button>
                    </div>
                </header>

                <main className="flex-1 p-4 md:p-6 overflow-y-auto overflow-x-hidden">
                    <Outlet />
                </main>
            </div>

            <SessionTimeoutModal loginPath="/portal/owner/login" />
        </div>
    );
};

export default OwnerLayout;
